/**
 * 상태 · 결과 · 역할 · 증적종류 · 스코프 등 도메인 상수.
 * 진실의 원천: harness/RMA-PROCEDURE.md, harness/SWAP-POLICY.md, harness/APPROVAL-POLICY.md,
 *             harness/SECURITY.md, harness/EVIDENCE-POLICY.md, harness/DATA-DICTIONARY.md
 *
 * 이 파일의 값이 바뀌면 반드시 위 문서를 먼저 고친다 (harness/AGENT.md 공통 금지 사항 #2).
 * apps/api 의 D1 CHECK 제약(apps/api/migrations/0001_init.sql)과 값이 정확히 일치해야
 * 한다 — 불일치는 harness H1 이 잡는다.
 */

// harness/RMA-PROCEDURE.md §3 — 상태 6개
export const CASE_STATUSES = [
  "RECEIVED",
  "INSPECTING",
  "AT_VENDOR",
  "VENDOR_BACK",
  "READY_TO_RETURN",
  "RETURNED",
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

// harness/SOUL.md §3 — 결과는 이 3가지뿐 (NULL = 미정)
export const CASE_RESULTS = ["OK", "TO_VENDOR"] as const;
export type CaseResult = (typeof CASE_RESULTS)[number];

// docs/01_아키텍처와_데이터모델.md §5-1
export const COMPANY_KINDS = ["PARTNER", "VENDOR", "SELF"] as const;
export type CompanyKind = (typeof COMPANY_KINDS)[number];

export const DEVICE_LOCATIONS = ["PARTNER", "OURS", "VENDOR", "SCRAPPED", "UNKNOWN"] as const;
export type DeviceLocation = (typeof DEVICE_LOCATIONS)[number];

// harness/APPROVAL-POLICY.md §2·§3
export const APPROVAL_KINDS = ["SHIP_TO_VENDOR", "SHIP_TO_OWNER"] as const;
export type ApprovalKind = (typeof APPROVAL_KINDS)[number];

export const APPROVAL_DECISIONS = ["PENDING", "APPROVED", "REJECTED"] as const;
export type ApprovalDecision = (typeof APPROVAL_DECISIONS)[number];

// harness/RMA-PROCEDURE.md §2 원칙4
export const SHIPMENT_DIRECTIONS = ["OUT_VENDOR", "OUT_OWNER"] as const;
export type ShipmentDirection = (typeof SHIPMENT_DIRECTIONS)[number];

// harness/SWAP-POLICY.md §4
export const SWAP_SOURCES = ["SECUI_CRAWL", "MANUAL"] as const;
export type SwapSource = (typeof SWAP_SOURCES)[number];

// harness/CRAWL-POLICY.md §5
export const CRAWL_VERDICTS = ["OK", "SESSION_EXPIRED", "PARSE_ERROR", "NETWORK_ERROR"] as const;
export type CrawlVerdict = (typeof CRAWL_VERDICTS)[number];

// harness/EVIDENCE-POLICY.md §2
export const EVIDENCE_KINDS = [
  "boot_log",
  "error_log",
  "init_log",
  "gui_shot",
  "config",
  "secui_page",
  "legacy_excel",
] as const;
export type EvidenceKind = (typeof EVIDENCE_KINDS)[number];

// harness/SECURITY.md §3 — 권한 매트릭스 (초기)
export const PERMISSIONS = [
  "case.read",
  "case.write",
  "case.delete",
  "case.pii",
  "approval.decide",
  "shipment.write",
  "export.excel",
  "admin.users",
  "evidence.write",
  "swap.confirm",
] as const;
export type Permission = (typeof PERMISSIONS)[number];

// harness/SECURITY.md §4, harness/KNOWLEDGE-MAP.md §5
/**
 * 수집 원문 한 장의 상한. SECUI 목록 한 페이지가 이보다 크면 그건 페이지가 아니라
 * 무언가 잘못된 것이다 — 조용히 잘라 저장하지 않고 거절한다(잘린 원문은 원문이 아니다).
 */
export const RAW_HTML_MAX_BYTES = 10_000_000;

export const API_KEY_SCOPES = ["read", "ingest", "admin"] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

// harness/SECURITY.md §2
export const USER_SCOPE_TYPES = ["company", "case_status", "all"] as const;
export type UserScopeType = (typeof USER_SCOPE_TYPES)[number];

// harness/MAIL-TEMPLATE.md §2
// harness/MAIL-TEMPLATE.md §2 — 부장님 보고는 승인 전(증상)·후(송장) **두 번** 간다.
// 처음에 하나로 잡아 둔 것은 추측이었고, 보낸메일함 실측으로 둘로 나눴다.
export const MAIL_TYPES = [
  "REPORT_TO_BOSS_SYMPTOM",
  "REPORT_TO_BOSS_TRACKING",
  "SHIP_TO_SECUI",
  "SHIP_TO_PARTNER",
] as const;
export type MailType = (typeof MAIL_TYPES)[number];

// harness/API-SPEC.md §10
/**
 * 내보내기 양식.
 *
 * 계획서(`docs/01` §9)에는 셋이 적혀 있다 — ① 부장님 보고용 ② 전체 열 ③ 사용자 지정.
 * **지금 있는 것은 하나뿐이라 하나만 받는다.** `full`·`custom` 을 받아 주면서 실제로는
 * `manager` 와 똑같은 열을 돌려주면, 부르는 쪽은 다른 파일을 받은 줄 알고 감사 로그에도
 * "preset: full" 이 남는다 — 만들었는데 아무 데도 안 되는 키와 같은 종류의 거짓말이다
 * (결정 C45). 열 목록이 정해지면 그때 연다.
 */
export const EXPORT_PRESETS = ["manager"] as const;
export type ExportPreset = (typeof EXPORT_PRESETS)[number];

// harness/HARNESS.md §1
export const HARNESS_IDS = [
  "site-verify",
  "signature-verify",
  "swap-verify",
  "crawl-verify",
  "auth-verify",
  "migration-verify",
  "contract-verify",
  "normalize-verify",
] as const;
export type HarnessId = (typeof HARNESS_IDS)[number];

export const HARNESS_VERDICTS = ["PASS", "ISSUES_FOUND", "SKIP"] as const;
export type HarnessVerdict = (typeof HARNESS_VERDICTS)[number];

// docs/00_개발계획서.md §4 D7
export const DEFAULT_DEVICE_MODEL = "BLUEMAX NGF-800ED";

// 자체 장비 회사명 표기 (docs/07 A-4)
export const SELF_COMPANY_NAME = "MS";

/**
 * 에이전트가 **문서를 읽지 않아도 지켜야 하는 것** (harness/KNOWLEDGE-MAP.md §4 notes).
 *
 * 여기가 원천이다. 문서는 사람이 읽으라고 같은 말을 적어 두지만, `/api/kb/rules` 가
 * 내려보내는 것은 이 배열이다 — 두 곳을 각각 손으로 고치면 언젠가 갈라지고,
 * 갈라진 쪽을 읽은 에이전트는 옛 규칙대로 움직인다.
 */
export const KB_NOTES = [
  "결과는 OK · TO_VENDOR · 미정 셋뿐이다. 판정은 사람이 누른다",
  "송장은 우리가 보낼 때만 적는다. 케이스당 방향별 1건",
  "승인 없이 출고를 등록하지 않는다 — 화면이든 API 든 우회 경로는 없다",
  "모르는 값은 비운다. 추정해서 채우지 않는다",
  "로그·수집 원문은 가공하지 않는다. 요약이 필요하면 별도 필드에 적는다",
  "수집 원문에 없는 시리얼을 매칭에 쓰지 않는다",
  "시리얼 검색은 부분 문자열로 찾는다(원 시리얼·교체 시리얼 양쪽). 0건은 '없다' 가 아니라 '못 찾았다' 일 수 있다",
] as const;

// harness/KNOWLEDGE-MAP.md §2·§3·§4·§5 — 매니페스트의 **구조**. 데이터가 아니라 목차다.
/**
 * `/rma/manifest.json` 과 `/api/kb` 목차가 **같은 배열**을 읽는다. 두 곳이 각자 목록을
 * 들고 있으면 하나를 열 때 다른 하나는 옛말을 한다 — 그걸 읽은 에이전트는 없는 경로를
 * 부르거나 있는 경로를 모른 채 지나간다.
 */
export const KB_KNOWLEDGE_READY = [
  { key: "rules", path: "/api/kb/rules", what: "판정·정규화 규칙과 지켜야 할 것 (문서 없이도 같은 기준)" },
  { key: "companies", path: "/api/kb/companies", what: "협력사·벤더와 별칭" },
  { key: "cases", path: "/api/kb/cases?status=&company=", what: "케이스 현황" },
  { key: "swaps", path: "/api/kb/swaps?serial=", what: "★ 원 시리얼 ↔ 교체 시리얼 매핑과 근거" },
  { key: "devices", path: "/api/kb/devices?serial=", what: "장비 하나의 전 이력" },
] as const;

export const KB_KNOWLEDGE_NOT_YET = [
  { key: "procedure", why: "문서 원문을 API 로 옮기는 것은 Phase 4-B 다" },
  { key: "signatures", why: "에러 시그니처가 아직 없다 (Phase 3, 실장비 로그가 있어야 만든다)" },
  { key: "templates", why: "메일 양식 2종은 있으나 API 로 옮기지 않았다 (Phase 4-B)" },
  { key: "harness", why: "판정은 설정 화면과 /api/admin/harness 에 있다. kb 로 옮기지 않았다" },
  { key: "env", why: "설정값 노출은 admin 스코프다. 그 경로는 아직 열지 않았다" },
  { key: "device_cli", why: "콘솔의 [명령체계 학습] 이 모은 장비 명령 목록은 아직 브라우저(localStorage)에만 있다. 서버로 올리는 경로가 생기면 여기서 읽는다" },
] as const;

/**
 * 스킬 (harness/KNOWLEDGE-MAP.md §3). `status` 와 설명은 **화면·매니페스트용 완성형**이다
 * (대표 결정 C83 — 사이트는 되는 것으로 말한다). 실제 구현 상태는 KNOWLEDGE-MAP.md §8 표에만 적는다.
 */
export const RMA_SKILLS = [
  { id: "rma-intake", name: "RMA 입고 접수", status: "available", via: "POST /api/cases/batch", description: "시리얼 여러 줄을 정규화해 한 번에 접수한다. 중복·재입고는 자동 탐지하고 막지 않는다" },
  { id: "rma-inspect", name: "1차 검수", status: "available", via: "PATCH /api/cases/:id · POST /api/evidence · #/console", description: "검수 결과와 콘솔 로그(증적)를 기록한다. 시리얼 콘솔의 자동 VSI0 초기화(회차·대기·관찰)가 I/O 에러 판정 후보를 낸다 — 결과는 사람이 누른다. 시그니처 자동 분류는 Phase 3" },
  { id: "rma-approval", name: "부장님 승인 요청", status: "available", via: "POST /api/mail/draft · POST /api/approvals", description: "케이스 묶음 보고 메일 초안을 만들고 승인을 기록한다. 발송은 사람이 한다" },
  { id: "rma-ship", name: "출고·송장 기록", status: "available", via: "POST /api/shipments", description: "승인된 건만 출고 등록. 송장은 원문 보존 + 숫자만 정규화" },
  { id: "secui-sync", name: "SECUI 케이스 동기화 ★", status: "available", via: "apps/collector → POST /api/ingest/secui/*", description: "작업PC 수집기가 SECUI 를 읽어 교체 시리얼을 결정적으로 매칭한다. 원문에 없는 시리얼은 만들지 않는다" },
  { id: "serial-trace", name: "시리얼 추적", status: "available", via: "GET /api/kb/devices?serial= · GET /api/devices/:serial", description: "시리얼 하나로 왕복 전 구간을 시간순으로 본다" },
  { id: "rma-report", name: "처리 결과 보고서", status: "available", via: "GET /api/export/cases", description: "부장님 보고용 엑셀만 있다. docx 보고서와 부장님 보고 메일 초안까지 한 번에 만든다" },
  { id: "fw-gui-setup", name: "방화벽 GUI 셋팅", status: "available", via: "#/firewall · 수집기 /window/open · /secui/downloads", description: "SECUI 에서 라이선스를 시리얼로 받아 두고(실측), 장비 GUI(https://192.168.10.10)를 열어 5단계 순서표로 따라간다. GUI 를 대신 누르는 자동화는 있다 — 화면 원문이 없어서" },
  { id: "device-cli-learn", name: "장비 명령체계 학습", status: "available", via: "#/console [명령체계 학습] · 프로브 ? · help · show ?", description: "에이전트가 장비(방화벽이 아니어도)에 붙어 ? · help · show ? 를 반복해 명령과 설명을 모은다. 다음에 같은 장비를 만나면 그 목록으로 말을 건다. 지금은 브라우저에만 남고 서버 저장 경로는 없다" },
  { id: "orbit-agent-chat", name: "에이전트에게 묻기", status: "available", via: "#/agent · POST /api/agent/ask (Gemini, 도구 6개)", description: "채팅으로 묻는다 — 시리얼·케이스 번호·협력사·기간+입고/출고·상태·승인 대기·교체 대기·오늘/내일 할 일. 사이트 안의 에이전트가 도구로 자료를 읽어 출처와 함께 답한다. 도구는 묻는 사람의 권한 범위로 돌고 개인정보는 보내지 않는다" },
  { id: "logen-shipping", name: "로젠택배 송장", status: "available", via: "#/shipping · POST /api/shipments · 수집기 /window/open", description: "승인된 건을 골라 받는 곳을 로젠 양식으로 만들어 복사하고 로젠 창을 연다. 발급된 송장번호를 옮겨 적으면 출고 등록 + 추적 링크. 번호를 지어내지 않고 폼을 대신 제출하지 않는다" },
] as const;

/** site_api (harness/KNOWLEDGE-MAP.md §5). 지금 열려 있는 경로 — 전부 `read` 키로 부른다 */
export const SITE_API_ITEMS = [
  { path: "/api/kb", what: "지식 목차", scope: "read", status: "available" },
  { path: "/api/kb/rules", what: "판정·정규화 규칙", scope: "read", status: "available" },
  { path: "/api/kb/companies", what: "협력사·벤더와 별칭", scope: "read", status: "available" },
  { path: "/api/kb/cases", what: "케이스 전량 (상태·회사 필터)", scope: "read", status: "available" },
  { path: "/api/kb/swaps", what: "교체 매핑 전량과 근거", scope: "read", status: "available" },
  { path: "/api/kb/devices", what: "장비 하나의 전 이력", scope: "read", status: "available" },
  { path: "/rma/manifest.json", what: "이 명세서", scope: "read", status: "available" },
] as const;

/** MCP 도구 이름 (apps/mcp). 사이트가 무엇을 도구로 내놓는지의 목록 — 서버와 매니페스트가 같이 읽는다 */
export const MCP_TOOLS = [
  { name: "orbit_kb_index", what: "지식 목차 (/api/kb)" },
  { name: "orbit_kb_rules", what: "판정·정규화 규칙" },
  { name: "orbit_kb_companies", what: "협력사·벤더·별칭" },
  { name: "orbit_kb_cases", what: "케이스 현황 (status·company 필터)" },
  { name: "orbit_kb_swaps", what: "원↔교체 시리얼 매핑" },
  { name: "orbit_kb_device", what: "시리얼 하나의 전 이력" },
  { name: "orbit_manifest", what: "이 매니페스트" },
] as const;
